/**
 * Questo file definisce la pagina AdminDashboard.
 * - È una pagina accessibile solo agli utenti con ruolo 'admin'.
 * - Il suo scopo è permettere agli amministratori di visualizzare le cartelle che gli utenti
 * hanno reso pubbliche e che sono in attesa di approvazione.
 * - Mostra un elenco di "card di approvazione", una per ogni cartella pendente.
 * - Ogni card mostra i dettagli della cartella (nome, autore) e il numero di flashcard.
 * - Cliccando su una card, è possibile espanderla per visualizzare le flashcard contenute.
 * - Fornisce un interruttore (toggle switch) per approvare o disapprovare una cartella.
 * - Le chiamate API per recuperare i dati e aggiornare lo stato di approvazione sono gestite
 * direttamente nel componente, non tramite Redux, come scelta implementativa per questa sezione specifica.
 */

import React, { useEffect, useState } from 'react';
//import { Link } from 'react-router-dom'; # DA RIMUOVERE se non usato
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import FlashcardItem from '../components/FlashcardItem';
import './AdminDashboard.css';

// Funzione helper per le chiamate API specifiche dell'admin.
// Semplifica il fetch e la gestione base degli errori.
const fetchClient = async (endpoint) => {
    const response = await fetch(`/api/admin/${endpoint}`);
    if (!response.ok) throw new Error('Errore di rete o di autorizzazione.');
    return response.json();
};

// Funzione per inviare la richiesta di approvazione/disapprovazione.
const toggleApproval = async (folderId) => {
    const response = await fetch('/api/admin/approve_folder.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_id: folderId }),
    });
    if (!response.ok) throw new Error('Impossibile aggiornare lo stato.');
};

/**
 * Sub-componente per la singola card di approvazione.
 * @param {object} props - Props del componente.
 * @param {object} props.folder - L'oggetto cartella da visualizzare.
 * @param {function} props.onToggleApproval - Callback da chiamare quando l'admin cambia lo stato di approvazione.
 */
const ApprovalCard = ({ folder, onToggleApproval }) => {
    const collapseId = `folder-approval-${folder.id}`;
    const flashcards = folder.flashcards || [];

    return (
        <div className="col-12">
            <div className="approval-card-item">
                <div className="approval-card-header">
                    {/* Area cliccabile per espandere/collassare la vista delle card */}
                    <div className="header-main-content" data-bs-toggle="collapse" data-bs-target={`#${collapseId}`}>
                        <i className="bi bi-folder2-open folder-icon"></i>
                        <div className="folder-details">
                            <span className="folder-name">{folder.nome}</span>
                            <span className="owner-info">Da: {folder.proprietario_email}</span>
                        </div>
                        <span className="badge rounded-pill bg-light text-dark ms-auto">{flashcards.length} card</span>
                    </div>
                    {/* Azioni, come il toggle di approvazione */}
                    <div className="header-actions">
                        <div className="approval-toggle">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`switch-${folder.id}`}
                                    checked={!!folder.approvata} // 'checked' vuole un booleano
                                    onChange={() => onToggleApproval(folder.id)}
                                />
                                <label className="form-check-label" htmlFor={`switch-${folder.id}`}>
                                    {folder.approvata ? 'Approvata' : 'In attesa'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Contenuto collassabile che mostra le flashcard */}
                <div className="collapse" id={collapseId}>
                    <div className="approval-card-body">
                        {flashcards.length > 0 ? (
                            <div className="row g-3">
                                {flashcards.map(card => (
                                    <div className="col-md-6 col-lg-4" key={card.id}>
                                        {/* isPublicView=true nasconde i pulsanti di modifica/elimina */}
                                        <FlashcardItem card={card} isPublicView={true} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted text-center p-3">Questa cartella è vuota.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Componente pagina principale AdminDashboard.
 */
export default function AdminDashboard() {
    // Stato per memorizzare l'elenco delle cartelle da approvare.
    const [folders, setFolders] = useState([]);
    // Stato per il caricamento dei dati.
    const [loading, setLoading] = useState(true);
    // Stato per eventuali errori.
    const [error, setError] = useState(null);

    // Funzione per caricare le cartelle dal backend.
    const fetchFolders = async () => {
        try {
            setLoading(true);
            const data = await fetchClient('list_pending_folders.php');
            setFolders(data.folders || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // useEffect per caricare i dati al montaggio del componente.
    useEffect(() => {
        fetchFolders();
    }, []);

    /**
     * Gestore per il toggle di approvazione.
     * - Aggiorna lo stato localmente per un feedback UI immediato (aggiornamento ottimistico).
     * - Invia la richiesta al backend.
     * - Se la richiesta fallisce, ripristina lo stato precedente per riflettere la realtà.
     * @param {number} folderId - L'ID della cartella modificata.
     */
    const handleToggle = async (folderId) => {
        // Aggiornamento ottimistico dell'interfaccia
        setFolders(currentFolders =>
            currentFolders.map(f =>
                f.id === folderId ? { ...f, approvata: !f.approvata } : f
            )
        );
        try {
            await toggleApproval(folderId); // Chiamata API
        } catch (err) {
            setError('Impossibile aggiornare la cartella. Riprova.');
            // Rollback in caso di errore
            setFolders(currentFolders =>
                currentFolders.map(f =>
                    f.id === folderId ? { ...f, approvata: !f.approvata } : f
                )
            );
        }
    };

    return (
        <>
            {loading && <LoadingSpinner message="Caricamento dati..." />}
            <Navbar />
            <div className="admin-dashboard-container">
                <div className="container">
                    <a href="/dashboard" className="page-back-link">
                        <i className="bi bi-arrow-left-circle"></i>
                        <span> Torna alla Dashboard</span>
                    </a>
                    <div className="text-center">
                        <h1 className="display-5 fw-bold mb-4">Pannello Approvazione</h1>
                        <p className="text-muted">Gestisci le cartelle pubbliche inviate dagli utenti. Clicca su una cartella per vedere le card.</p>
                    </div>

                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="row g-4">
                        {folders.length > 0 ? (
                            folders.map(folder => (
                                <ApprovalCard key={folder.id} folder={folder} onToggleApproval={handleToggle} />
                            ))
                        ) : (
                            // Messaggio mostrato quando non ci sono cartelle da approvare.
                            <div className="col-12">
                                <div className="text-center p-5 empty-state">
                                    <i className="bi bi-check2-circle display-1 text-success mb-3"></i>
                                    <h4>Tutto approvato!</h4>
                                    <p className="lead text-muted">Non ci sono nuove cartelle in attesa di revisione.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}